import Vue from 'vue'
import apiClient from '@v/apiClient.js'
import {
  propEq,
  filter,
  compose,
  not,
  replace,
  toUpper,
  findIndex,
  ifElse,
  always,
  identity,
  pipe,
  equals,
  map,
  lensIndex,
  set,
  flip,
  chain,
  forEach,
  isEmpty,
  lensProp,
} from 'ramda'

const capitalize = replace(/^./, toUpper)

export const namespaced = true

export const state = {
  isFetchingDeals: false,
  isFetchingProducts: false,
  isFetchingResources: false,
  fetchError: null,
  resources: null,
  products: null,
  deals: null,
  resourcesWereFetched: false,
  productsWereFetched: false,
  dealssWereFetched: false,
}

export const getters = {}

export const mutations = {
  setFetchingDeals: (state, isFetchingDeals) => (state.isFetchingDeals = isFetchingDeals),
  setFetchingProducts: (state, isFetchingProducts) =>
    (state.isFetchingProducts = isFetchingProducts),
  setFetchingResources: (state, isFetchingResources) =>
    (state.isFetchingResources = isFetchingResources),
  setResources: (state, resources) => Vue.set(state, 'resources', resources),
  setProducts: (state, products) => Vue.set(state, 'products', products),
  setDeals: (state, deals) => Vue.set(state, 'deals', deals),
  setFetchError: (state, error) => (state.fetchError = error),
  setResourcesWereFetched: state => (state.resourcesWereFetched = true),
  setProductsWereFetched: state => (state.productsWereFetched = true),
  setDealsWereFetched: state => (state.dealsWereFetched = true),
  addResource: (state, resource) => state.resources.push(resource),
  addProduct: (state, product) => state.products.push(product),
  addDeal: (state, deal) => state.deals.push(deal),
}

const updateAction = name => ({ commit, state }, item) => {
  const namePlural = name + 's'
  if (!state[namePlural]) return
  const capitalizedName = capitalize(name)
  const capitalizedNamePlural = capitalize(namePlural)
  const resourceMatches = propEq('Key', item.Key)
  return chain(i => items =>
    i === -1
      ? commit(`add${capitalizedName}`, item)
      : commit(`set${capitalizedNamePlural}`, set(lensIndex(i), item, items)),
  )(findIndex(resourceMatches))(state[namePlural])
}

export const actions = {
  removeProduct: ({ commit, state }, key) => {
    commit('setProducts', filter(compose(not, propEq('Key', key)), state.products))
  },
  updateResource: ({ commit, state }, data) => {
    try {
      updateAction('resource')({ commit, state }, data.resource)
      if (state.products) {
        const updatedProducts = setProductsActiveStateBasedOnResource(state.products, data.resource)
        commit('setProducts', updatedProducts)
      }
      commit('setResources', data.resourceList)
    } catch (e) {
      throw new Error(e)
    }
  }, // todo: update resource group also affects subresources
  updateProduct: updateAction('product'),
  updateDeal: updateAction('deal'),

  maybeFetchResources: async ({ dispatch, state }) => {
    if (!state.isFetchingResources && !state.resourcesWereFetched) await dispatch('fetchResources')
  },
  maybeFetchProducts: async ({ dispatch, state }) => {
    if (!state.isFetchingProducts && !state.productsWereFetched) await dispatch('fetchProducts')
  },
  maybeFetchDeals: async ({ dispatch, state }) => {
    if (!state.isFetchingDeals && !state.dealsWereFetched) await dispatch('fetchDeals')
  },
  fetchResources: async ({ commit }) =>
    await fetchInventoryPart({
      name: 'resources',
      urlParameters: 'includeInactive=true',
      commit,
    }),
  fetchProducts: async ({ commit }) => await fetchInventoryPart({ name: 'products', commit }),
  fetchDeals: async ({ commit }) => await fetchInventoryPart({ name: 'deals', commit }),
}

const fetchInventoryPart = async ({ name, urlParameters = '', commit }) => {
  const capitalizedName = capitalize(name)
  commit(`setFetching${capitalize(name)}`, true)
  try {
    const queryParams = urlParameters ? '?' + urlParameters : ''
    const response = await apiClient.get(`${name}${queryParams}`)
    commit(`set${capitalizedName}`, response.data)
    commit(`set${capitalizedName}WereFetched`)
  } catch (e) {
    commit('setFetchError', e)
    console.error(e)
  }
  commit(`setFetching${capitalizedName}`, false)
}

// Helper functions

const setProductsActiveStateBasedOnResource = (products, resource) =>
  map(p => {
    if (p.ResourceKey === resource.Key) p.IsActive = resource.IsActive
    if (resource.ChildResources)
      forEach(r => {
        if (p.ResourceKey === r.Key) p.IsActive = resource.IsActive
      })(resource.ChildResources)
    return p
  })(products)

const getUpdatedResourcesActiveState = (editedResource, resources) =>
  map(
    ifElse(
      propEq('ParentResourceKey', editedResource.Key),
      set(lensProp('IsActive'), editedResource.IsActive),
      identity,
    ),
  )(resources)
